import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Button, Tooltip } from '@pwc/appkit-react';

import SDKCustomSelect from '../../shared/forms/sdkCustomSelect/sdkCustomSelect.component';
import Loading from '../../shared/displayComponents/loading.component';
import Dropzone from '../../shared/forms/dropzone/dropzone.container';
import { periodSelector, taxYearSelector } from '../../shared/store/selectors';
import styles from '../shared/dataImportPage.module.scss';
import { REJECT_OPTION_VALUE, DOCUMENT_ACCEPTED_TYPES } from '../constants';
import UploadStatus from '../uploadStatus/uploadStatus.component';
import ProcessingUpload from '../uploadStatus/processingUpload.component';
import { Jobs } from '../../shared/enums/job';

import ImportDataGrid from './importDataGrid/importDataGrid.component';
import scopedStyles from './genericAccountDataUpload.module.scss';
import useGenericAccountDataUploadJob from './hooks/useGenericAccountDataUploadJob.hook';

const UPDATE_DATA_OPTION_VALUE = 'updateData';

const DATA_OPTIONS = [
  {
    label: 'Update Data',
    value: UPDATE_DATA_OPTION_VALUE,
  },
];

const DATA_OPTIONS_TOOLTIP =
  'Data Options are fixed to Update Data for generic account data uploads.';

const DUPLICATE_OPTIONS = [
  { value: REJECT_OPTION_VALUE, label: 'Reject' },
  { value: 'aggregate', label: 'Aggregate' },
];

const GenericAccountDataUpload = ({ taxYear, period }) => {
  const [file, setFile] = useState(null);
  const [fileBuffer, setFileBuffer] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [duplicateOption, setDuplicateOption] = useState(REJECT_OPTION_VALUE);

  const jobType = Jobs.GENERIC_ACCOUNT_DATA_UPLOAD;

  const {
    hdrId,
    batchId,
    uniqueJobId,
    isValidationError,
    isPartialImport,
    isGridVisible,
    fetchLastImportDataPage,
    startUpload,
    isJobInProgress,
    isStartingJob,
    isJobFinished,
    isUploadActive,
    jobData,
    resetJob,
    lastFileUploadDisplayTime,
    uploadFinishedDisplayTime,
    fileBackedGridRows,
    isResolvingFileRows,
  } = useGenericAccountDataUploadJob({ jobType, taxYear, period });

  const currentJobData = jobData();
  const isUploadSectionVisible = Boolean(
    isUploadActive || currentJobData || isStartingJob || isJobInProgress,
  );
  const isDisabled = isJobInProgress;

  const onDrop = useCallback(
    droppedFile => {
      resetJob();
      setFile(droppedFile);
      setFileBuffer(null);
      setIsFileLoading(true);
    },
    [resetJob],
  );

  const onFileBufferLoaded = useCallback(loadedFileBuffer => {
    setFileBuffer(loadedFileBuffer);
    setIsFileLoading(false);
  }, []);

  const uploadFile = useCallback(async () => {
    const shouldAggregateData = duplicateOption !== REJECT_OPTION_VALUE;

    await startUpload({
      file,
      fileBuffer,
      fileName: 'genericAccountData',
      jsonData: {
        taxYear,
        period,
        shouldReplaceAccountData: false,
        shouldAggregateData,
      },
    });

    setFile(null);
  }, [duplicateOption, taxYear, period, file, fileBuffer, startUpload]);

  const handleDuplicateOptionsChange = useCallback(({ value }) => {
    setDuplicateOption(value);
  }, []);

  return (
    <div className={styles.uploadFormSection}>
      <div className="row">
        <div className={`col ${styles.fileFormat}`}>
          <p className={styles.fileFormatHeader}>File Format:</p>
          <p>org_id, account_id, jurisdiction_id, beg_end_flag, amount</p>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Dropzone
            className={styles.dropzone}
            value={file}
            onDropCallback={onDrop}
            onFileBufferLoaded={onFileBufferLoaded}
            acceptTypes={DOCUMENT_ACCEPTED_TYPES}
            disabled={isDisabled}
          />
        </div>
      </div>
      <div className={`row ${styles.uploadFormDropdown}`}>
        <div className="col">
          <Tooltip
            content={DATA_OPTIONS_TOOLTIP}
            placement="top"
            mouseEnterDelay={500}
            tooltipTheme="light"
            hasArrow
            className={scopedStyles.dataOptionsTooltip}
          >
            <span className={scopedStyles.dataOptionsTooltipTarget}>
              <SDKCustomSelect
                appkitLabel="Data Options"
                className="sdk-custom-select"
                options={DATA_OPTIONS}
                value={UPDATE_DATA_OPTION_VALUE}
                disabled
                virtualized
              />
            </span>
          </Tooltip>
        </div>
        <div className="col">
          <SDKCustomSelect
            appkitLabel="Duplicate Options"
            className="sdk-custom-select"
            options={DUPLICATE_OPTIONS}
            value={duplicateOption}
            onChange={handleDuplicateOptionsChange}
            disabled={isDisabled}
            virtualized
          />
        </div>
        <div className="col">
          <Button
            size="lg"
            disabled={isFileLoading || !file || !fileBuffer || isJobInProgress}
            onClick={uploadFile}
          >
            Upload
          </Button>
        </div>
      </div>
      {isUploadSectionVisible && (
        <div className={scopedStyles.uploadSection}>
          {isStartingJob && !isUploadActive ? (
            <Loading isLoading />
          ) : currentJobData ? (
            <UploadStatus
              job={currentJobData}
              isValidationError={isValidationError}
              isPartialImport={isPartialImport}
              lastFileUploadDisplayTime={lastFileUploadDisplayTime}
              uploadFinishedDisplayTime={uploadFinishedDisplayTime}
            >
              {isGridVisible && (
                <ImportDataGrid
                  key={hdrId || batchId || uniqueJobId}
                  fetchLastImportDataPage={fetchLastImportDataPage}
                  rowData={
                    isResolvingFileRows || fileBackedGridRows ? fileBackedGridRows || [] : null
                  }
                  isGridLoading={isResolvingFileRows}
                />
              )}
            </UploadStatus>
          ) : isUploadActive && !isJobFinished ? (
            <ProcessingUpload />
          ) : null}
        </div>
      )}
    </div>
  );
};

GenericAccountDataUpload.propTypes = {
  taxYear: PropTypes.string,
  period: PropTypes.string,
};

export default connect(state => ({
  taxYear: taxYearSelector(state),
  period: periodSelector(state),
}))(GenericAccountDataUpload);
